/**
 * Default rule set for common Israeli merchants.
 *
 * Matched against `merchantNormalized`, which is uppercased with punctuation
 * folded to spaces and trailing branch numbers/city suffixes removed — so
 * "רמי לוי- קדימה" is matched by the needle "רמי לוי", and "APPLE.COM/BILL"
 * by "APPLE COM".
 *
 * **Order is load-bearing and specific rules must come first.** "סלקום אנרג'י"
 * is an electricity supplier while "סלקום" is a phone company; "רמי לוי תקשורת"
 * is a mobile carrier while "רמי לוי" is a supermarket. Put the general rule
 * first and the specific one can never fire.
 */

import type { Rule } from '../domain/types.ts';

interface Seed {
  name: string;
  /**
   * Plain substrings. Hebrew needles are often deliberate prefixes — "מסעד"
   * is meant to catch "מסעדת", "צרכני" to catch "צרכנייה" — so these are NOT
   * word-bounded.
   */
  needles?: string[];
  /**
   * Needles that must match as a whole word. Required for short tokens that
   * are substrings of unrelated names: "HOT" inside "HOTEL", "ACE" inside
   * "PLACE", "דלק" inside a longer word. Used sparingly, because bounding a
   * needle also stops it matching intended suffixes — "SPOTIFY" must stay
   * unbounded to catch "SPOTIFYIL".
   */
  wholeWord?: string[];
  categoryId: string;
  subcategoryId?: string;
}

/**
 * Read top to bottom as priority order. The narrow, easily-shadowed entries are
 * deliberately grouped at the top.
 */
const SEEDS: Seed[] = [
  // --- Must precede their more general namesakes -------------------------
  // Each of these is a substring of, or is shadowed by, a broader rule below.
  { name: 'סלקום אנרג\'י', needles: ['סלקום אנרג'], categoryId: 'cat_utilities', subcategoryId: 'cat_utilities_electricity' },
  { name: 'רמי לוי תקשורת', needles: ['רמי לוי תקשורת'], categoryId: 'cat_utilities', subcategoryId: 'cat_utilities_telecom' },
  // "ביטוח לאומי" contains "ביטוח"; without this it files as insurance.
  { name: 'ביטוח לאומי', needles: ['ביטוח לאומי'], categoryId: 'cat_government' },
  // "מחסני חשמל" contains "חשמל"; without this it files as an electricity bill.
  { name: 'מחסני חשמל', needles: ['מחסני חשמל'], categoryId: 'cat_shopping', subcategoryId: 'cat_shopping_electronics' },

  // --- Supermarkets ------------------------------------------------------
  {
    name: 'סופרמרקטים',
    needles: ['שופרסל', 'רמי לוי', 'ויקטורי', 'יינות ביתן', 'אושר עד', 'טיב טעם', 'מגה בעיר', 'יוחננוף', 'קרפור', 'סופר יודה', 'פפאיה מרקט', 'קשת טעמים'],
    categoryId: 'cat_groceries',
  },
  {
    name: 'מכולת ונוחות',
    needles: ['צרכני', 'סופר נוחות', 'AM PM', 'טיב טעם אקספרס', 'מכולת'],
    categoryId: 'cat_groceries',
    subcategoryId: 'cat_groceries_convenience',
  },
  {
    name: 'מאפיות',
    needles: ['מאפיי', 'ארגז לחם', 'באגט', 'לחם ארז', 'טאבון'],
    categoryId: 'cat_groceries',
    subcategoryId: 'cat_groceries_bakery',
  },

  // --- Eating out --------------------------------------------------------
  {
    name: 'משלוחי אוכל',
    needles: ['וולט', 'WOLT', 'תן ביס', '10BIS', 'תןביס', 'מישלוח'],
    categoryId: 'cat_dining',
    subcategoryId: 'cat_dining_delivery',
  },
  {
    name: 'בתי קפה',
    needles: ['קפה', 'ARCAFFE', 'לנדוור', 'גרג', 'COFIX', 'קופיקס', 'רולדין'],
    categoryId: 'cat_dining',
    subcategoryId: 'cat_dining_cafe',
  },
  {
    name: 'מסעדות',
    needles: ['מסעד', 'בורגר', 'פיצה', 'סושי', 'המבורגר', 'ביסטרו', 'פאב', 'שווארמה', 'פלאפל', 'הומוס'],
    categoryId: 'cat_dining',
  },

  // --- Transport ---------------------------------------------------------
  {
    name: 'תחנות דלק',
    needles: ['סונול', 'דור אלון', 'אלונית'],
    wholeWord: ['פז', 'דלק', 'טן', 'YELLOW'],
    categoryId: 'cat_transport',
    subcategoryId: 'cat_transport_fuel',
  },
  {
    name: 'טעינת רכב חשמלי',
    needles: ['טעינה', 'TESLA', 'גרין דרייב', 'ELECTREON'],
    wholeWord: ['EVI', 'CHARGE'],
    categoryId: 'cat_transport',
    subcategoryId: 'cat_transport_ev',
  },
  {
    name: 'כבישי אגרה וחניה',
    needles: ['דרך ארץ', 'כביש 6', 'פנגו', 'PANGO', 'CELLOPARK', 'סלופארק', 'אחוזת חוף', 'חניון'],
    categoryId: 'cat_transport',
    subcategoryId: 'cat_transport_tolls',
  },
  {
    name: 'תחבורה ציבורית',
    needles: ['רב קו', 'רכבת ישראל', 'אגד', 'מטרופולין', 'גט טקסי'],
    wholeWord: ['דן', 'GETT'],
    categoryId: 'cat_transport',
    subcategoryId: 'cat_transport_public',
  },

  // --- Utilities ---------------------------------------------------------
  {
    name: 'חשמל',
    needles: ['חברת חשמל', 'חשמל'],
    categoryId: 'cat_utilities',
    subcategoryId: 'cat_utilities_electricity',
  },
  {
    name: 'מים',
    needles: ['תאגיד מים', 'מי אביבים', 'מקורות', 'מי שקמה', 'מי נתניה'],
    categoryId: 'cat_utilities',
    subcategoryId: 'cat_utilities_water',
  },
  {
    name: 'תקשורת',
    needles: ['בזק', 'פרטנר', 'סלקום', 'גולן טלקום', 'PARTNER', 'CELLCOM', 'טריפל'],
    // HOT is a substring of HOTEL, YES of many names, 019 of any number.
    wholeWord: ['HOT', 'YES', '019'],
    categoryId: 'cat_utilities',
    subcategoryId: 'cat_utilities_telecom',
  },

  // --- Insurance and health ---------------------------------------------
  {
    name: 'ביטוח',
    needles: ['ביטוח', 'הפניקס', 'איילון', 'מגדל', 'הראל', 'מנורה', 'שלמה ביטוח'],
    // "כלל" is a whole word; "כללית" is a different company (a health fund).
    wholeWord: ['כלל', 'AIG'],
    categoryId: 'cat_insurance',
  },
  {
    name: 'בתי מרקחת',
    needles: ['סופר פארם', 'ניו פארם', 'SUPERPHARM'],
    wholeWord: ['BE'],
    categoryId: 'cat_health',
    subcategoryId: 'cat_health_pharmacy',
  },
  {
    name: 'כושר וספורט',
    needles: ['ג\'ים', 'הולמס פלייס', 'קאנטרי', 'בריכ', 'סטודיו'],
    wholeWord: ['GYM', 'ICON'],
    categoryId: 'cat_health',
    subcategoryId: 'cat_health_fitness',
  },
  {
    name: 'בריאות',
    needles: ['מכבי', 'כללית', 'מאוחדת', 'לאומית שירותי', 'ד"ר', 'מרפא', 'רופא', 'שיניים'],
    categoryId: 'cat_health',
  },

  // --- Travel ------------------------------------------------------------
  {
    name: 'טיסות',
    needles: ['ארקיע', 'אל על', 'EL AL', 'ישראייר', 'RYANAIR', 'WIZZ', 'טיסות', 'AIRLINES', 'תעופה'],
    categoryId: 'cat_travel',
    subcategoryId: 'cat_travel_flights',
  },
  {
    name: 'מלונות ולינה',
    needles: ['BOOKING', 'BKG', 'AIRBNB', 'EXPEDIA', 'HOTEL', 'מלון', 'צימר', 'AGODA'],
    categoryId: 'cat_travel',
    subcategoryId: 'cat_travel_hotels',
  },
  {
    name: 'השכרת רכב',
    needles: ['HERTZ', 'AVIS', 'SIXT', 'EUROPCAR', 'קל אוטו', 'אלדן', 'השכרת רכב'],
    categoryId: 'cat_travel',
    subcategoryId: 'cat_travel_car_rental',
  },
  { name: 'תיירות', needles: ['תיירות', 'נופש', 'TRAVEL'], categoryId: 'cat_travel' },

  // --- Subscriptions and entertainment ----------------------------------
  {
    name: 'מנויים דיגיטליים',
    needles: ['NETFLIX', 'SPOTIFY', 'YOUTUBE', 'APPLE COM', 'GOOGLE', 'DISNEY', 'AMAZON PRIME', 'MICROSOFT', 'OPENAI', 'ANTHROPIC', 'ICLOUD'],
    categoryId: 'cat_entertainment',
    subcategoryId: 'cat_entertainment_subscriptions',
  },
  {
    name: 'פנאי',
    needles: ['רשות הטבע', 'סינמה', 'יס פלאנט', 'הבימה', 'מוזיאון', 'תיאטרון', 'הופע'],
    categoryId: 'cat_entertainment',
  },

  // --- Government --------------------------------------------------------
  {
    name: 'ממשלה ומיסים',
    needles: ['ביטוח לאומי', 'מס הכנסה', 'עיריי', 'מועצה מקומית', 'רשות המיסים', 'דואר ישראל'],
    categoryId: 'cat_government',
  },

  // --- Shopping ----------------------------------------------------------
  {
    name: 'אלקטרוניקה',
    needles: ['אלקטרו', 'IVORY'],
    wholeWord: ['KSP', 'באג'],
    categoryId: 'cat_shopping',
    subcategoryId: 'cat_shopping_electronics',
  },
  {
    name: 'ביגוד',
    needles: ['קסטרו', 'פוקס', 'זארה', 'H&M', 'רנואר', 'גולף', 'TERMINAL X', 'אמריקן איגל'],
    categoryId: 'cat_shopping',
    subcategoryId: 'cat_shopping_clothing',
  },
  {
    name: 'בית ומשק בית',
    needles: ['איקאה', 'IKEA', 'הום סנטר', 'כלי בית', 'ורדינון'],
    // ACE is a substring of PLACE.
    wholeWord: ['ACE'],
    categoryId: 'cat_home',
  },
];

export function defaultRules(): Rule[] {
  return SEEDS.map((seed, index) => ({
    id: `rule_default_${index}`,
    order: index,
    enabled: true,
    name: seed.name,
    match: {
      merchant: {
        // A single alternation keeps one rule per concept, so reordering or
        // disabling "supermarkets" is one action rather than twelve.
        mode: 'regex' as const,
        value: seedPattern(seed),
      },
    },
    assign: {
      categoryId: seed.categoryId,
      ...(seed.subcategoryId === undefined ? {} : { subcategoryId: seed.subcategoryId }),
    },
  }));
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Letters that count as "inside a word", in both scripts we care about. */
const WORD_CHAR = 'A-Za-z\\u0590-\\u05FF';

function seedPattern(seed: Seed): string {
  const parts = [
    ...(seed.needles ?? []).map(escapeRegex),
    ...(seed.wholeWord ?? []).map(
      (needle) => `(?<![${WORD_CHAR}])${escapeRegex(needle)}(?![${WORD_CHAR}])`,
    ),
  ];
  return parts.join('|');
}
